import { useDocumentViewer } from '@st/document-viewer-kit'
import { Range, STDocument } from '@st/sdk'
import {
  Button,
  DocumentSplitDialog,
  DocumentSplitList,
  DocumentSplitListItem,
  PopoverMenu,
  TagChip
} from '@st/theme'
import { deepEqual } from '@st/util/deep-equal'
import { clsx } from 'clsx'
import { useEffect, useState } from 'react'
import {
  selAlreadySplitDocuments,
  selFolderOperationInProgress,
  stFolderModule
} from '~/features/st-folder-viewer/st-folder-module'
import { useProcess, useProcessState } from '~/store'

type Props = {
  doc: STDocument
}
export function SplitDocumentBanner({ doc }: Props) {
  const documentViewer = useDocumentViewer()

  const stFolder = useProcess(stFolderModule)

  const folderId = useProcessState(stFolderModule, (s) => s.folderState!.folderId)
  const documentTypes = useProcessState(stFolderModule, (s) => s.folderState!.documentTypes ?? [])
  const isSplitting = useProcessState(stFolderModule, (s) =>
    selFolderOperationInProgress(s, 'folders/splitBundledDocument')
  )
  const alreadySplitDocuments = useProcessState(stFolderModule, (s) =>
    selAlreadySplitDocuments(s, doc.id)
  )

  const [selectedRanges, setSelectedRanges] = useState(
    doc.splits
      .map((split) => split.pageRange)
      // filter out documents that were already split
      .filter((split) => !alreadySplitDocuments.some((d) => deepEqual(d.source?.pageRange, split)))
  )

  const [isOpen, setIsOpen] = useState(false)

  // In some cases, the document splits can be modified.
  // When that happens (for example, if you add a page break),
  // we want to update the selected ranges to reflect the new splits
  //
  // For example, if you have pages 2..6 and you add a page break at page 4,
  // the splits will be updated to 2..3 and 4..6.
  //
  // In this case, we want to keep 2..3 and 4..6 selected because previously they 4..6,
  // the containing range was 2..6.
  useEffect(() => {
    const newSelectedRanges = doc.splits
      .map((split) => split.pageRange)
      .filter((candidate) => selectedRanges.some((r) => rangeOverlaps(r, candidate)))

    setSelectedRanges(newSelectedRanges)
  }, [doc.splits])

  return (
    <div
      className={clsx(
        'pointer-events-auto flex items-center justify-between gap-x-6 bg-yellow-300 px-5 text-base text-yellow-900'
      )}
    >
      We detected multiple documents bundled into one and have predicted the splits for you. You can
      modify the splits by clicking between the pages.
      <PopoverMenu
        isControlled={true}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        offset={{ dy: 8 }}
        trigger={
          <Button variant="default" className="bg-white">
            Split document
          </Button>
        }
      >
        <DocumentSplitDialog
          title="Split document"
          subtitle="Select the documents you would like to extract into separate files. You can modify the splits by clicking between the pages. This original document will remain."
          buttons={
            <>
              <Button variant="subtle" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  stFolder.send({
                    type: 'request',
                    request: {
                      type: 'folders/splitBundledDocument',
                      folderId: folderId,
                      sourceDocumentId: doc.id,
                      ranges: selectedRanges
                    }
                  })
                }}
                disabled={isSplitting || selectedRanges.length === 0}
              >
                {isSplitting ? 'Splitting...' : 'Split document'}
              </Button>
            </>
          }
        >
          <DocumentSplitList>
            {doc.splits.map((split, index) => {
              const label =
                documentTypes.find((dt) => dt.id === split.inferredDocumentTypeId)?.name ??
                'Uncategorized'

              const selected = selectedRanges.some((r) => deepEqual(r, split.pageRange))

              const existingSplitDocument = alreadySplitDocuments.find((d) =>
                deepEqual(d.source?.pageRange, split.pageRange)
              )

              return (
                <DocumentSplitListItem
                  key={index}
                  title={formatPageRange(split.pageRange)}
                  description={
                    label == 'Uncategorized'
                      ? formatPageRange(split.pageRange)
                      : `${formatPageRange(split.pageRange)} | ${label}`
                  }
                  selected={selected}
                  tag={
                    existingSplitDocument ? (
                      <TagChip
                        color="green"
                        onClick={() =>
                          stFolder.send({
                            type: 'docSelected',
                            docId: existingSplitDocument.id
                          })
                        }
                      >
                        Already split
                      </TagChip>
                    ) : null
                  }
                  onClickJump={() => {
                    documentViewer.send({
                      type: 'scrollToPage',
                      pageNumber: split.pageRange.first
                    })
                  }}
                  onToggleSelected={() => {
                    if (selected) {
                      const newRanges = selectedRanges.filter((r) => !deepEqual(r, split.pageRange))
                      setSelectedRanges(newRanges)
                    } else {
                      setSelectedRanges(
                        [...selectedRanges, split.pageRange].toSorted((a, b) => a.first - b.first)
                      )
                    }
                  }}
                />
              )
            })}
          </DocumentSplitList>
        </DocumentSplitDialog>
      </PopoverMenu>
    </div>
  )
}

function formatPageRange(pageRange: { first: number; last: number }) {
  return pageRange.first === pageRange.last
    ? `Page ${pageRange.first}`
    : `Pages ${pageRange.first} - ${pageRange.last}`
}

function rangeOverlaps(a: Range, b: Range) {
  if (a.last < b.first) return false
  if (a.first > b.last) return false
  return true
}
